/**
 * AchievementNotification - Solid.js
 * Zeigt neue Achievements als Notification an
 */
import { maxColonySpecies } from '../utils/achievementText';
import { createSignal, onMount, onCleanup, Show, For, createMemo } from 'solid-js';
import { useTranslation } from '../context/LanguageContext';
import axios from '../config/axios';
import { css } from '../../styled-system/css';

interface AchievementNotificationProps {
  onClose?: () => void;
}

export default function AchievementNotification(props: AchievementNotificationProps) {
  const { t } = useTranslation();
  const [newAchievements, setNewAchievements] = createSignal<any[]>([]);
  const [currentIndex, setCurrentIndex] = createSignal(0);

  const checkUnnotified = async () => {
    try {
      const response = await axios.get('/api/achievements/unnotified');
      if (response.data?.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setNewAchievements(prev => [...prev, ...response.data.data]);
      }
    } catch {
      // silent
    }
  };

  onMount(() => {
    checkUnnotified();
    const interval = setInterval(checkUnnotified, 30000);
    onCleanup(() => clearInterval(interval));
  });

  // Timer für Slide-Through
  onMount(() => {
    const checkSlideThrough = () => {
      if (newAchievements().length === 0) return;

      const timer = setTimeout(() => {
        setCurrentIndex((prev) => {
          if (prev < newAchievements().length - 1) return prev + 1;
          setTimeout(() => props.onClose?.(), 5000);
          return prev;
        });
      }, 5000);
      onCleanup(() => clearTimeout(timer));
    };

    checkSlideThrough();
  });

  const getRarityClass = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'rarity-legendary';
      case 'epic': return 'rarity-epic';
      case 'rare': return 'rarity-rare';
      default: return 'rarity-common';
    }
  };

  const translateAchievement = (a: any) => {
    if (!a?.code) return { name: a?.name || '', description: a?.description || '' };
    if (a.code.startsWith('max_workers_')) {
      const speciesName = maxColonySpecies(a.name || '');
      return {
        name: t('achievements.matureColony', { species: speciesName }),
        description: t('achievements.matureColonyDescription', { species: speciesName })
      };
    }
    return { name: a.name || '', description: a.description || '' };
  };

  const currentAchievement = createMemo(() => newAchievements()[currentIndex()]);
  const rarity = createMemo(() => currentAchievement() ? getRarityClass(currentAchievement().rarity) : 'rarity-common');

  const baseNotificationStyle = css({
    position: 'fixed',
    top: '80px',
    right: '20px',
    zIndex: 'zIndex.modal-backdrop',
    backgroundColor: 'colors.card-bg',
    border: '3px solid colors.border',
    borderRadius: '12px',
    padding: '1rem',
    minWidth: '300px',
    maxWidth: '400px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    animation: 'slideInRight 0.5s ease-out',
    transition: 'all 0.3s ease',
    '@media (max-width: 768px)': {
      top: '60px',
      right: '10px',
      left: '10px',
      maxWidth: 'none',
      minWidth: 'auto',
    },
  });

  const rarityStyles: Record<string, string> = {
    'rarity-legendary': css({
      borderColor: 'colors.premium-gold',
      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, colors.card-bg 100%)',
      boxShadow: '0 8px 32px rgba(255, 215, 0, 0.4)',
    }),
    'rarity-epic': css({
      borderColor: '#9c27b0',
      background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, colors.card-bg 100%)',
      boxShadow: '0 8px 32px rgba(156, 39, 176, 0.4)',
    }),
    'rarity-rare': css({
      borderColor: '#2196f3',
      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, colors.card-bg 100%)',
      boxShadow: '0 8px 32px rgba(33, 150, 243, 0.4)',
    }),
    'rarity-common': css({
      borderColor: 'colors.success',
      boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
    }),
  };

  const contentStyle = css({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    position: 'relative',
  });

  const iconStyle = css({ fontSize: '3rem', flexShrink: 0 });
  const textStyle = css({ flex: 1 });
  const titleStyle = css({
    fontWeight: '700',
    color: 'colors.text',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '1rem',
    '& i': { color: 'colors.success' },
  });
  const achievementNameStyle = css({ fontSize: '1.1rem', fontWeight: '600', color: 'colors.text', marginBottom: '0.25rem' });
  const achievementDescStyle = css({ fontSize: '0.85rem', color: 'colors.text', opacity: 0.8 });
  const closeButtonStyle = css({
    position: 'absolute',
    top: '-0.5rem',
    right: '-0.5rem',
    backgroundColor: 'colors.card-bg',
    border: '2px solid colors.border',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    color: 'colors.text',
    fontSize: '0.85rem',
    _hover: {
      backgroundColor: 'colors.error',
      borderColor: 'colors.error',
      color: 'white',
      transform: 'scale(1.1)',
    },
  });
  const indicatorStyle = css({
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid colors.border',
  });
  const dotStyle = css({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'colors.border',
    transition: 'all 0.3s ease',
  });
  const activeDotStyle = css({ backgroundColor: 'colors.primary', transform: 'scale(1.2)' });

  return (
    <Show when={newAchievements().length > 0 && currentAchievement()}>
      <div class={`${baseNotificationStyle} ${rarityStyles[rarity()]}`}>
        <div class={contentStyle}>
          <div class={iconStyle}>{currentAchievement()!.icon || '🏅'}</div>
          <div class={textStyle}>
            <div class={titleStyle}>
              <i class="fas fa-trophy"></i>
              {t('achievements.newAchievementUnlocked')}
            </div>
            <div class={achievementNameStyle}>{translateAchievement(currentAchievement()!).name}</div>
            <div class={achievementDescStyle}>{translateAchievement(currentAchievement()!).description}</div>
          </div>
          <button
            type="button"
            class={closeButtonStyle}
            onClick={() => {
              if (currentIndex() < newAchievements().length - 1) {
                setCurrentIndex(i => i + 1);
              } else {
                setNewAchievements([]);
                setCurrentIndex(0);
                props.onClose?.();
              }
            }}
            aria-label={t('achievements.close')}
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <Show when={newAchievements().length > 1}>
          <div class={indicatorStyle}>
            <For each={newAchievements()}>
              {(_, i) => (
                <div
                  class={`${dotStyle} ${i() === currentIndex() ? activeDotStyle : ''}`}
                />
              )}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  );
}
